import { useState, useEffect, useRef } from "react";
import { Search, Clock, TrendingUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/hooks/useLanguage";

interface SearchSuggestion {
  type: 'product' | 'category' | 'recent' | 'popular';
  text: string;
  productId?: string;
  image?: string;
}

interface SearchAutocompleteProps {
  onSearch?: (query: string) => void;
}

export const SearchAutocomplete = ({ onSearch }: SearchAutocompleteProps) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('recentSearches');
    if (saved) {
      setRecentSearches(JSON.parse(saved));
    }
  }, []);

  // Save to recent searches
  const saveToRecent = (searchQuery: string) => {
    const updated = [searchQuery, ...recentSearches.filter(s => s !== searchQuery)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
  };

  // Fetch suggestions
  useEffect(() => {
    if (!query.trim()) {
      if (isOpen && recentSearches.length > 0) {
        setSuggestions(recentSearches.map(text => ({ type: 'recent', text })));
      } else {
        setSuggestions([]);
      }
      return;
    }

    const fetchSuggestions = async () => {
      const normalizedQuery = query.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      
      // All available categories
      const allCategories = [
        { key: 'electronique', fr: 'Électronique', en: 'Electronics' },
        { key: 'mode', fr: 'Mode', en: 'Fashion' },
        { key: 'maison', fr: 'Maison & Jardin', en: 'Home & Garden' },
        { key: 'beaute', fr: 'Beauté & Santé', en: 'Beauty & Health' },
        { key: 'sport', fr: 'Sport & Loisirs', en: 'Sports & Leisure' },
        { key: 'livre', fr: 'Livres', en: 'Books' }
      ];

      const matches: SearchSuggestion[] = [];
      
      // Check if query matches any category name
      allCategories.forEach(cat => {
        const catNameFr = cat.fr.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const catNameEn = cat.en.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const catKey = cat.key.toLowerCase();
        
        if (catNameFr.includes(normalizedQuery) || catNameEn.includes(normalizedQuery) || catKey.includes(normalizedQuery)) {
          const translatedName = language === 'fr' ? cat.fr : cat.en;
          matches.push({
            type: 'category',
            text: translatedName,
            productId: cat.key // Store category key for navigation
          });
        }
      });
      
      // Fetch matching products
      const { data: products } = await supabase
        .from('products')
        .select('id, title, images, category, brand')
        .eq('active', true)
        .limit(10);

      if (products) {
        const productMatches: SearchSuggestion[] = [];
        
        products.forEach((product: any) => {
          const title = product.title?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
          const brand = product.brand?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';

          // Product matches
          if (title.includes(normalizedQuery) || brand.includes(normalizedQuery)) {
            productMatches.push({
              type: 'product',
              text: product.title,
              productId: product.id,
              image: product.images?.[0]
            });
          }
        });

        // Prioritize categories, then products
        matches.push(...productMatches);
      }

      setSuggestions(matches.slice(0, 8));
    };

    const timer = setTimeout(fetchSuggestions, 150);
    return () => clearTimeout(timer);
  }, [query, isOpen, recentSearches, language]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleSelectSuggestion(suggestions[selectedIndex]);
        } else if (query.trim()) {
          handleSearch(query);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion: SearchSuggestion) => {
    if (suggestion.type === 'product' && suggestion.productId) {
      navigate(`/product/${suggestion.productId}`);
      saveToRecent(suggestion.text);
    } else if (suggestion.type === 'category' && suggestion.productId) {
      // Navigate to category page
      navigate(`/category/${suggestion.productId}`);
      saveToRecent(suggestion.text);
    } else {
      handleSearch(suggestion.text);
    }
    setIsOpen(false);
    setQuery('');
  };

  // Handle search
  const handleSearch = (searchQuery: string) => {
    const trimmed = searchQuery.trim();
    if (trimmed) {
      saveToRecent(trimmed);
      navigate(`/search?q=${encodeURIComponent(trimmed)}`);
      setIsOpen(false);
      if (onSearch) onSearch(trimmed);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        !inputRef.current?.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative flex-1 max-w-md mx-8">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSearch(query);
        }}
        className="relative"
      >
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none z-10" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
            setSelectedIndex(-1);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={t('header.search.placeholder')}
          className="w-full h-10 pl-10 pr-4 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </form>

      {/* Autocomplete Dropdown */}
      {isOpen && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-1 bg-background border border-border rounded-md shadow-lg max-h-96 overflow-y-auto z-50"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              onClick={() => handleSelectSuggestion(suggestion)}
              className={`w-full px-4 py-2.5 text-left hover:bg-muted transition-colors flex items-center gap-3 ${
                selectedIndex === index ? 'bg-muted' : ''
              }`}
            >
              {suggestion.type === 'recent' && (
                <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              )}
              {suggestion.type === 'popular' && (
                <TrendingUp className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              )}
              {suggestion.type === 'product' && suggestion.image && (
                <img
                  src={suggestion.image}
                  alt=""
                  className="h-10 w-10 object-cover rounded flex-shrink-0"
                />
              )}
              {suggestion.type === 'product' && !suggestion.image && (
                <div className="h-10 w-10 bg-muted rounded flex-shrink-0" />
              )}
              {suggestion.type === 'category' && (
                <Search className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              )}
              <span className="text-sm flex-1 truncate">
                {suggestion.text}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
